/**
 * @file Single source of truth for the offline IndexedDB schema.
 *
 * Every offline-cache helper (`offlineGame`, `offlinePack`, `offlineUserProgress`,
 * `offlineChallenge`) must open the database via this module's `getDb()`. Defining the
 * `upgrade` callback in more than one place is a latent race: whichever module wins the
 * first `openDB` call decides which object stores get created — if a module with a partial
 * schema wins the race, the rest of the app's offline reads fail silently.
 *
 * To add or remove an object store: bump `DB_VERSION`, add the store name to `STORE_NAMES`,
 * and let the `upgrade` loop pick it up automatically.
 */

import { openDB, type IDBPDatabase } from 'idb'

const DB_NAME = 'AllezAllezDB'
const DB_VERSION = 3

export const STORE_NAMES = ['gameState', 'challenges', 'packs', 'userProgress', 'cart'] as const
export type StoreName = (typeof STORE_NAMES)[number]

export async function getDb(): Promise<IDBPDatabase> {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      for (const name of STORE_NAMES) {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { keyPath: 'id' })
        }
      }
    }
  })
}
